import { Image as TiptapImage } from 'tiptap-extensions'
import { Plugin } from 'tiptap'

import AscImageEditor from '@/components/atoms/AscImageEditor'

export const ImageDisplay = {
  INLINE: 'inline',
  BREAK_TEXT: 'block',
  FLOAT_LEFT: 'left',
  FLOAT_RIGHT: 'right',
}

export const DEFAULT_IMAGE_WIDTH = null
export const DEFAULT_IMAGE_DISPLAY = ImageDisplay.INLINE

function getAttrs(dom) {
  const { cssFloat, display } = dom.style
  let { width, height } = dom.style

  let displayAttr =
    dom.getAttribute('data-display') || dom.getAttribute('display')
  if (displayAttr) {
    displayAttr = /(inline|block|left|right)/.test(displayAttr)
      ? displayAttr
      : ImageDisplay.INLINE
  } else if (cssFloat === 'left' && !display) {
    displayAttr = ImageDisplay.FLOAT_LEFT
  } else if (cssFloat === 'right' && !display) {
    displayAttr = ImageDisplay.FLOAT_RIGHT
  } else if (!cssFloat && display === 'block') {
    displayAttr = ImageDisplay.BREAK_TEXT
  } else {
    displayAttr = ImageDisplay.INLINE
  }

  width = width || dom.getAttribute('width') || null
  height = height || dom.getAttribute('height') || null

  return {
    src: dom.getAttribute('src') || '',
    title: dom.getAttribute('title') || '',
    alt: dom.getAttribute('alt') || '',
    width: width == null ? null : parseInt(width, 10),
    height: height == null ? null : parseInt(height, 10),
    display: displayAttr,
  }
}

function toDOM(node) {
  const { src, alt, title, width, height, display } = node.attrs
  const attrs = {
    src,
    alt,
    title,
    width,
    height,
  }
  attrs['data-display'] = display
  return ['img', attrs]
}

export default class Image extends TiptapImage {
  constructor(options = {}) {
    super(options)
    this.allowImages = options.allowImages
    this.allowedImageTypes = options.allowedImageTypes
  }

  get defaultOptions() {
    return {
      defaultWidth: DEFAULT_IMAGE_WIDTH,
      defaultDisplay: DEFAULT_IMAGE_DISPLAY,
    }
  }

  get schema() {
    return {
      inline: true,
      attrs: {
        src: {
          default: '',
        },
        alt: {
          default: '',
        },
        title: {
          default: '',
        },
        width: {
          default:
            this.imageDefaultWidth > 0
              ? this.imageDefaultWidth
              : DEFAULT_IMAGE_WIDTH,
        },
        height: {
          default: null,
        },
        display: {
          default: /(inline|block|left|right)/.test(this.defaultDisplay)
            ? this.defaultDisplay
            : DEFAULT_IMAGE_DISPLAY,
        },
      },
      group: 'inline',
      draggable: true,
      parseDOM: [{ tag: 'img[src]', getAttrs }],
      toDOM,
    }
  }

  get plugins() {
    const allowImages = this.allowImages
    const allowedImageTypes = this.allowedImageTypes
    const isAllowedFileType = function (imageType) {
      // Allowed File types .png, .jpg,.jpeg
      const type = imageType.split('/')
      return type[0] === 'image' && allowedImageTypes.includes(`.${type[1]}`)
    }
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            paste(view, event) {
              const items = (
                event.clipboardData || event.originalEvent.clipboardData
              ).items

              for (let i = 0; i < items.length; i++) {
                const { schema } = view.state
                const image = items[i].getAsFile()
                // Return here, else copying texts won't work
                if (!image || !isAllowedFileType(image.type)) {
                  return
                }
                event.preventDefault()
                // Return false if its a image and if its not allowed
                if (!allowImages) {
                  return false
                }
                const reader = new FileReader()
                reader.onload = readerEvent => {
                  const node = schema.nodes.image.create({
                    src: readerEvent.target.result,
                  })
                  const transaction = view.state.tr.replaceSelectionWith(node)
                  view.dispatch(transaction)
                }
                reader.readAsDataURL(image)
              }
            },
            drop(view, event) {
              if (!allowImages) {
                event.preventDefault()
                return false
              }

              const hasFiles = event.dataTransfer.files.length > 0

              // dont allow Image drop from web tab because CORS wont allow convertion to base64
              const hasImageUrl = event.dataTransfer.getData('Text')

              const extRegex = new RegExp(
                `\\w+\\.(${allowedImageTypes
                  .join('|')
                  .replace(/\./g, '')})(?=\\?|$)`,
              )

              const isImageURL = hasImageUrl.match(extRegex) != null

              if (!hasFiles || isImageURL) {
                return
              }

              event.preventDefault()

              const images = event.dataTransfer.files
              const { schema } = view.state
              const coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              })

              for (let i = 0; i < images.length; i++) {
                const image = images[i]

                if (!isAllowedFileType(image.type)) {
                  return
                }

                const reader = new FileReader()
                reader.onload = readerEvent => {
                  const node = schema.nodes.image.create({
                    src: readerEvent.target.result,
                  })

                  const transaction = view.state.tr.insert(
                    coordinates.pos,
                    node,
                  )
                  view.dispatch(transaction)
                }
                reader.readAsDataURL(image)
              }
            },
          },
        },
      }),
    ]
  }

  get view() {
    return AscImageEditor
  }
}
